import { useEffect, useState } from 'react';
import { Calendar } from 'lucide-react';
import supabase from '../lib/supabase';
import { PageHeader, Section as UISection, Badge, EmptyState } from '../components/ui';

type Row = {
  id: string;
  booking_reference: string;
  scheduled_at: string;
  ends_at: string;
  lesson_type: string;
  environment: string;
  participants: number;
  status: string;
  coach_payout_amount: number;
  currency: string;
};

export default function CoachLessons({ coachId }: { coachId: string }) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('coach_bookings')
        .select(`
          id, booking_reference, scheduled_at, ends_at, lesson_type, environment,
          participants, status, coach_payout_amount, currency
        `)
        .eq('coach_id', coachId)
        .order('scheduled_at', { ascending: false })
        .limit(100);
      setRows((data as Row[]) ?? []);
      setLoading(false);
    })();
  }, [coachId]);

  const now = new Date();
  const upcoming = rows.filter((r) => new Date(r.scheduled_at) >= now);
  const past = rows.filter((r) => new Date(r.scheduled_at) < now);

  const totalUpcoming = upcoming
    .filter((r) => ['confirmed', 'pending_payment'].includes(r.status))
    .reduce((s, r) => s + Number(r.coach_payout_amount), 0);

  return (
    <div className="p-8 max-w-5xl">
      <PageHeader
        eyebrow="Coach"
        title="Lessons"
        subtitle={upcoming.length > 0
          ? `${upcoming.length} upcoming · ${upcoming[0]?.currency ?? 'AED'} ${totalUpcoming.toFixed(0)} expected`
          : 'Your booked, completed, and cancelled lessons.'
        }
      />

      {loading ? (
        <UISection className="p-0 overflow-hidden">
          <div className="p-10 text-sm text-gray-500 text-center">Loading lessons…</div>
        </UISection>
      ) : rows.length === 0 ? (
        <UISection className="p-0 overflow-hidden">
          <EmptyState
            icon={Calendar}
            title="No lessons yet"
            description="Once someone books one of your lessons, it'll show up here."
          />
        </UISection>
      ) : (
        <>
          {upcoming.length > 0 && (
            <UISection title="Upcoming" subtitle={`${upcoming.length} lesson${upcoming.length === 1 ? '' : 's'}`} className="p-0 overflow-hidden">
              <LessonTable rows={upcoming} />
            </UISection>
          )}
          {past.length > 0 && (
            <UISection title="Past" subtitle={`${past.length} lesson${past.length === 1 ? '' : 's'}`} className="p-0 overflow-hidden">
              <LessonTable rows={past} faded />
            </UISection>
          )}
        </>
      )}
    </div>
  );
}

function LessonTable({ rows, faded }: { rows: Row[]; faded?: boolean }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50/70 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
        <tr>
          <th className="text-left px-5 py-3 font-semibold">When</th>
          <th className="text-left px-5 py-3 font-semibold">Lesson</th>
          <th className="text-right px-5 py-3 font-semibold">Earnings</th>
          <th className="text-left px-5 py-3 font-semibold">Status</th>
          <th className="text-left px-5 py-3 font-semibold">Ref</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const start = new Date(r.scheduled_at);
          const end = new Date(r.ends_at);
          return (
            <tr key={r.id} className={`border-t border-gray-100 hover:bg-gray-50/60 ${faded ? 'text-gray-500' : ''}`}>
              <td className="px-5 py-3 whitespace-nowrap">
                <div className="text-gray-900">
                  {start.toLocaleDateString('en-GB', { weekday: 'short', month: 'short', day: 'numeric' })}
                </div>
                <div className="text-xs text-gray-500">
                  {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  {' – '}
                  {end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </td>
              <td className="px-5 py-3">
                <div className="capitalize">{r.lesson_type.replace('_', ' ')} · {r.environment}</div>
                <div className="text-xs text-gray-500">
                  {r.participants} {r.participants === 1 ? 'player' : 'players'}
                </div>
              </td>
              <td className="px-5 py-3 text-right font-medium">
                {r.currency} {Number(r.coach_payout_amount).toFixed(0)}
              </td>
              <td className="px-5 py-3">
                <Badge tone={
                  r.status === 'confirmed' ? 'green'
                  : r.status === 'completed' ? 'gray'
                  : r.status === 'cancelled' ? 'red'
                  : 'amber'
                }>{r.status.replace('_', ' ')}</Badge>
              </td>
              <td className="px-5 py-3 font-mono text-[11px] text-gray-500">
                {r.booking_reference}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
